import React, { Suspense, useEffect, useRef, useState } from "react";
import { FiAlignCenter, FiCheck } from "react-icons/fi";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Menu,
  Transition,
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
} from "@headlessui/react";
import { Fragment } from "react";
import { FaGift } from "react-icons/fa6";
import { useSelector, useDispatch } from "react-redux";
import { logout, setAccessToken } from "../../../Redux/Slices/AuthSlice";
import {
  setCartItemCount,
  addToCart,
  setTotalPrice,
} from "../../../Redux/Slices/AddToCartSlice";
import Types from "../../../Helpers/Interfaces/ProductsType";
import { persistor } from "../../../Redux/Store";
import Storage from "../../../Helpers/Storage";
import Config from "../../../Config";

import {
  useLazySearchProductsQuery,
  useLazyCustomerLogoutQuery,
} from "../../../Services/ProductService";
import { useToast } from "../../../Helpers/Hooks/useToast";
import GamingMegaMenu from "./GamingMegaMenu";
import { flagUrl } from "../../../Utils/flag";
const CountrySelector = React.lazy(() => import("./CountrySelector"));
const MyModal = React.lazy(() => import("../../Modal"));
const GiftsMegaMenu = React.lazy(() => import("./GiftsMegaMenu"));
const RefillsMegaMenu = React.lazy(() => import("./RefillsMegaMenu"));
const Cart = React.lazy(() => import("./Cart"));

declare global {
  interface Window {
    googleTranslateElementInit: () => void;
    google: {
      translate: {
        TranslateElement: new (options: any, containerId: string) => void;
      };
    };
  }
}

function Header() {
  const authData: any = useSelector((state: any) => state.AuthSlice);
  const selectedCountry: any = useSelector(
    (state: any) => state.SettingSlice.selectedCountry,
  );
  const toast = useToast();

  const isLogin = authData?.accessToken !== "";
  const [isLanguage, setIsLanguage] = useState<boolean>(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [dropdownNoOpen, setDropdownNoOpen] = useState<number>(0);
  const activeTabNumber = useRef<number>(0);
  const [selected, setSelected] = useState<any>(null);
  const [isLogoutLoading, setIsLogoutLoading] = useState<boolean>(false);
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cartItems: any = useSelector((state: any) => state.AddToCartSlice);
  const [logoutQueryTrigger] = useLazyCustomerLogoutQuery();
  const [searchProductsTrigger, { data, isFetching }] =
    useLazySearchProductsQuery();

  /**
   * @HANDEL_LOGOUT_FROM_ANOTHER_TAB
   */

  const path = window.location.hash.replace("#", "");
  const pathLower = path.toLowerCase();
  const isRefillActive = pathLower.includes("/refill");
  const isGamingActive =
    pathLower.includes("/gaming") || path.includes("Gaming");
  const isTravelActive = pathLower.includes("/travel");
  /*
   * International Top-up.
   *
   * A top-level category in the catalogue now, a peer of Gift Cards, Gaming
   * and Phone Refill, so it gets its own route rather than hanging off a
   * sub-category name that Admin can rename at any time.
   *
   * Still the same listing screen underneath -- the two differ only in which
   * parent they list, and a second copy would be a second thing to keep in
   * step with the first.
   */
  const isInternationalActive = pathLower.includes("/international-topup");
  const isGiftCardsActive =
    pathLower.includes("gift-cards") &&
    !isGamingActive &&
    !isTravelActive &&
    !isRefillActive;
  const isGiftCardsSubRoute =
    pathLower.includes("gift-cards/") &&
    !isGamingActive &&
    !isTravelActive &&
    !isRefillActive;

  useEffect(() => {
    const syncLogout = (event: StorageEvent) => {
      if (event.key === "logout-event") {
        // Perform logout logic here

        // Clear auth state, redirect to login, etc.
        handleLogout();
      }
    };

    window.addEventListener("storage", syncLogout);
    return () => window.removeEventListener("storage", syncLogout);
  }, []);

  // Sample cart items - replace with your actual cart data
  const SkeletonCard = () => {
    return (
      <>
        {[...Array(4)].map((_, index) => (
          <Combobox.Option
            key={`skelton-${index}`}
            className={({ active }) =>
              `cursor-pointer select-none p-2 w-full ${
                active
                  ? "bg-blue-500 text-white"
                  : "text-gray-900 dark:text-gray-200"
              }`
            }
            value={index}
          >
            <div
              key={index}
              className="flex flex-wrap items-center justify-start gap-4 cursor-pointer animate-pulse mb-2"
            >
              <div className="w-12 h-12 bg-gray-300 dark:bg-gray-600 rounded-lg ml-3" />
              <div>
                <div className="h-4 w-24 bg-gray-300 dark:bg-gray-600 rounded-md" />
                <div className="h-3 w-32 bg-gray-300 dark:bg-gray-600 rounded-md mt-1" />
              </div>
            </div>
          </Combobox.Option>
        ))}
      </>
    );
  };

  const handleSearch = async (event: React.ChangeEvent<HTMLInputElement>) => {
    await searchProductsTrigger(`?keyword=${event.target.value}`);
  };

  const handleLogout = async () => {
    setIsLogoutLoading(true);
    const refreshToken = Storage.get(`${Config.STORAGE_KEY}/refreshToken`);
    await logoutQueryTrigger(refreshToken).unwrap();
    Storage.set("logout-event", Date.now().toString());
    Storage.remove(`persist:auth`);
    dispatch(logout());
    persistor.purge(); // Clear persisted state
    Storage.remove(`${Config.STORAGE_KEY}/accessToken`);
    Storage.remove(`${Config.STORAGE_KEY}/auth`);
    Storage.remove(`${Config.STORAGE_KEY}/refreshToken`);
    Storage.remove(`${Config.STORAGE_KEY}/accessTokenExpiry`);

    dispatch(addToCart([]));
    dispatch(setCartItemCount(0));
    dispatch(setTotalPrice(0));
    dispatch(setAccessToken({ accessToken: "", refreshToken: "" }));
    toast.success("Logout Successfully.", 2000);
    setIsLogoutLoading(false);
    navigate("/auth/signin");
  };

  const handleNavigate = (item: any) => {
    if (
      item === "Wishlist" ||
      item === "Orders" ||
      item === "My Account" ||
      item === "Tickets"
    ) {
      if (item === "Wishlist") {
        navigate("/wishlist");
      } else if (item === "Orders") {
        navigate("/my-orders");
      } else if (item === "My Account") {
        navigate("/my-account");
      } else {
        navigate("/my-tickets");
      }
    } else {
      navigate(
        `/gift-cards/product-details/${item?._id}/${item?.productName}`,
        { replace: true },
      );
      setIsOpen(false);
    }
  };

  const getUsername = (e: string) => {
    if (typeof e !== "string" || !e.includes("@")) return e;
    return e.split("@")[0].trim();
  };

  const handleSearchProduct = (event: any) => {
    if (
      event.key === "Enter" &&
      event?.target?.value &&
      event.target.value.trim() !== ""
    ) {
      // ✅ check if products are available
      if (data?.products && data.products.length > 0) {
        setIsOpen(false);
        navigate(`/gift-cards/${event.target.value}`);
      } else {
        // do nothing or show a toast/snackbar
        // toast.error("No search results found");
      }
    }
  };

  const handleLanguageChange = (code: string) => {
    const tryChange = (retries = 5) => {
      const selectEl =
        document.querySelector<HTMLSelectElement>(".goog-te-combo");

      if (selectEl) {
        selectEl.value = code;
        selectEl.dispatchEvent(new Event("change"));
      } else if (retries > 0) {
        setTimeout(() => tryChange(retries - 1), 500);
      } else {
        console.warn(
          "Google Translate dropdown not found after multiple attempts",
        );
      }
    };

    tryChange();
  };

  return (
    <>
      <header className="flex flex-col shadow-md pt-3 bg-white dark:bg-dark-background font-[sans-serif] min-h-[70px] transition-colors">
        <div className={"sm:px-10 md:px-12 px-4 lg:px-16"}>
          <div className="flex flex-row justify-end p-2 absolute">
            {/* Required for Google Translate to inject its stuff */}
            <div id="google_translate_container" style={{ display: "none" }} />
          </div>

          <div className="flex w-full max-w-screen-xxl mx-auto">
            <div className="flex flex-wrap items-center justify-between relative lg:gap-y-4 gap-y-4 gap-x-4 w-full">
              <button
                className="md:hidden sidebar-menu-button"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <FiAlignCenter className="w-7 h-7 text-cyan-500 dark:text-cyan-400" />
              </button>
              <div className="flex items-center">
                <Link to="/" className="md:ml-4 ml-0">
                  <img
                    src={`${process.env.PUBLIC_URL}/imgs/logo.svg`}
                    alt="logo"
                    className="w-32 cursor-pointer dark:invert"
                  />
                </Link>
              </div>

              <div className="hidden md:flex bg-light-foreground dark:bg-dark-foreground items-center border max-md:order-1 border-transparent focus-within:border-black dark:focus-within:border-gray-600 focus-within:bg-transparent px-4 h-11 min-w-[40%] lg:w-2/4 max-md:w-full transition-all rounded-full dark:border-[#344a5e]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 192.904 192.904"
                  className="fill-gray-400 dark:fill-gray-500 mr-4 w-4 h-4"
                >
                  <path d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z"></path>
                </svg>
                {/* <input type='email' placeholder='Search...' className="w-full outline-none bg-transparent text-black dark:text-white text-sm placeholder:text-gray-500 dark:placeholder:text-gray-400" /> */}

                <Combobox
                  value={selected}
                  onChange={(item: any) => {
                    setSelected(item);
                    if (item && (item as any)?._id) {
                      handleNavigate(item);
                    }
                    setIsOpen(false);
                  }}
                >
                  <div className="relative w-full">
                    <div className="flex items-center gap-2">
                      <Combobox.Input
                        className="w-full outline-none bg-transparent text-black dark:text-white text-sm placeholder:text-gray-500 dark:placeholder:text-gray-400"
                        placeholder="Search..."
                        autoComplete="off"
                        displayValue={() => query}
                        onChange={(event) => {
                          setQuery(event.target.value);
                          handleSearch(event); // Your search logic
                          setIsOpen(true); // Open the dropdown immediately when typing
                        }}
                        onFocus={() => {
                          if (query.trim()) setIsOpen(true);
                        }} // Only open dropdown if there's a query
                        onBlur={() => {
                          // Delay closing dropdown to allow click selection
                          setTimeout(() => setIsOpen(false), 150);
                        }}
                        onClick={() => {
                          if (query.trim()) setIsOpen(true);
                        }} // Only open dropdown if there's a query
                        onKeyDown={(event) => {
                          if (event.key !== "Enter") {
                            // Handle other key events (e.g., typing to search)
                            handleSearchProduct(event);
                          }
                        }}
                      />
                      {query && (
                        <button
                          onClick={() => {
                            setQuery("");
                            setIsOpen(false);
                          }}
                          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                          title="Clear search"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      )}
                    </div>

                    <Transition
                      show={isOpen}
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="opacity-0"
                      enterTo="opacity-100"
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Combobox.Options
                        onMouseLeave={() => setIsOpen(false)} // Close dropdown when mouse leaves
                        onMouseEnter={() => setIsOpen(true)} // Ensure dropdown stays open when mouse enters
                        onFocus={() => setIsOpen(true)} // Ensure dropdown remains open on focus
                        className={`absolute z-[999] mt-[10px] w-full bg-white dark:bg-dark-foreground border border-gray-300 dark:border-dark-foreground rounded-md shadow-2xl ${
                          isOpen ? "block" : "hidden"
                        }`}
                      >
                        {isFetching ? <SkeletonCard /> : null}

                        {data &&
                          data.products &&
                          data.products.length === 0 &&
                          !isFetching && (
                            <Combobox.Option
                              className="cursor-pointer select-none p-2 py-4 w-full border-t border-transparent text-gray-900 dark:text-gray-300"
                              disabled
                              value=""
                            >
                              No Search Results found
                            </Combobox.Option>
                          )}

                        {data &&
                          data.products &&
                          data.products.length > 0 &&
                          data.products.map(
                            (item: Types.ProductDetails, index: number) => (
                              <Combobox.Option
                                key={item._id}
                                // Selection (click or Enter) is handled via Combobox onChange
                                className={({ active }) =>
                                  `cursor-pointer select-none p-2 w-full border-t ${
                                    index === 0
                                      ? "border-transparent"
                                      : "border-gray-100 dark:border-gray-700"
                                  } ${
                                    active
                                      ? "bg-blue-100 dark:bg-blue-900 dark:text-gray-200"
                                      : "text-gray-900 dark:text-gray-200"
                                  }`
                                }
                                value={item}
                              >
                                <div className="flex flex-wrap items-center justify-start gap-4 cursor-pointer">
                                  <div className="w-12 h-12 overflow-hidden">
                                    <img
                                      src={item.productImage}
                                      className="max-w-full h-12 rounded-lg"
                                      alt={item.productName}
                                    />
                                  </div>

                                  <div className="max-w-[200px] overflow-hidden">
                                    <p className="text-[15px] text-gray-800 dark:text-gray-200 font-bold">
                                      {item.productName}
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate max-w-[180px]">
                                      {item.shortDescription}
                                    </p>
                                  </div>
                                </div>
                              </Combobox.Option>
                            ),
                          )}
                      </Combobox.Options>
                    </Transition>
                  </div>
                </Combobox>
              </div>

              <div className="flex items-center space-x-4 pr-2 max-md:ml-auto">
                <ul className="flex items-center">
                  <li className="max-md:hidden text-[15px] max-lg:py-2 px-4 font-medium text-gray-800 dark:text-gray-200 cursor-pointer">
                    <Link
                      to={isLogin ? "/helps" : "/auth/signin"}
                      className="flex items-center"
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icons/help.svg`}
                        className="w-8 h-8 mr-2 cursor-pointer fill-black dark:fill-white bg-gray-100 dark:bg-gray-500 p-2 rounded-full"
                        alt="header-help"
                      />
                      Help
                    </Link>
                  </li>
                  <li
                    onClick={() => setIsLanguage(true)}
                    className="md:px-4 px-0 flex items-center text-[15px] max-lg:py-2 font-medium text-gray-800 dark:text-gray-200 cursor-pointer"
                  >
                    <img
                      src={`${
                        flagUrl(selectedCountry?.value)
                      }`}
                      alt="header-country"
                      className="md:w-7 md:h-7 w-6 h-6 mr-2 cursor-pointer fill-black dark:fill-white bg-gray-100 dark:bg-gray-800 rounded-full"
                    />
                    <span className="max-md:hidden">EN</span>
                  </li>
                  <li className="flex items-center text-[15px] max-lg:py-2 md:px-4 px-0 font-medium text-gray-800 dark:text-gray-200 cursor-pointer">
                    {authData.isAuthenticated === false ? (
                      <Link
                        to={"/auth/signin"}
                        className="flex flex-row items-center"
                      >
                        <img
                          src={`${process.env.PUBLIC_URL}/icons/users.svg`}
                          className="md:w-7 md:h-7 w-6 h-6 mr-2 cursor-pointer fill-black dark:fill-white bg-gray-100 dark:bg-gray-800 rounded-full"
                          alt="header-users"
                        />
                        <span className="max-md:hidden">Login</span>
                      </Link>
                    ) : (
                      <Menu
                        as="div"
                        className="relative inline-block text-left z-50"
                      >
                        <Menu.Button className="relative">
                          <div className="space-x-10 flex items-center justify-center">
                            <div className="relative inline-block">
                              <img
                                src={`${process.env.PUBLIC_URL}/imgs/users.png`}
                                className="w-9 h-8 rounded-full sepia"
                                alt="header-users"
                              />
                              <span className="h-2 w-2 rounded-full border border-white bg-green-500 block absolute bottom-0 right-0"></span>
                            </div>
                          </div>
                        </Menu.Button>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 rounded-md bg-white dark:bg-dark-foreground shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="px-4 py-3">
                              <div className="flex flex-wrap flex-row items-center justify-start gap-4 cursor-pointer">
                                <img
                                  src={`${process.env.PUBLIC_URL}/imgs/users.png`}
                                  className="sepia w-13 h-12 rounded-full"
                                  alt="header-users"
                                />
                                <div>
                                  <p className="text-[15px] text-gray-800 dark:text-white font-bold truncate max-w-[210px]">
                                    {getUsername(
                                      authData?.authUser?.firstName +
                                        " " +
                                        authData?.authUser?.lastName ||
                                        authData?.authUser?.email,
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[210px] dark:text-gray-200">
                                    {authData?.authUser?.email}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="py-2 px-3">
                              {[
                                "My Account",
                                "Wishlist",
                                "Orders",
                                "Tickets",
                              ].map((item) => (
                                <Menu.Item key={item}>
                                  {({ active }) => (
                                    <div
                                      onClick={() => handleNavigate(item)}
                                      className={`${
                                        active
                                          ? "bg-gray-200 dark:bg-gray-700"
                                          : ""
                                      } cursor-pointer rounded-lg px-4 py-2 flex items-center gap-3`}
                                    >
                                      {item}
                                    </div>
                                  )}
                                </Menu.Item>
                              ))}
                            </div>

                            <div className="px-4 py-3">
                              <Link
                                to="#"
                                onClick={() => {
                                  if (!isLogoutLoading) {
                                    handleLogout();
                                  }
                                }}
                                // onClick={handleLogout}
                                className="w-full flex justify-center py-1 px-4 border border-transparent rounded-md text-md font-bold text-gray-800 dark:text-white focus:outline-none"
                              >
                                Sign out
                              </Link>
                            </div>
                          </Menu.Items>
                        </Transition>
                      </Menu>
                    )}
                  </li>
                  <li className="max-lg:py-2 md:px-4 cursor-pointer px-0">
                    <Menu
                      as="div"
                      className="relative inline-block text-left z-50"
                    >
                      <Menu.Button
                        className="relative"
                        onClick={() => setIsLoading(true)}
                        id="my-itopup-cart"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="md:w-6 md:h-6 w-5 h-5 inline text-gray-800 dark:text-gray-200"
                          viewBox="0 0 512 512"
                        >
                          <path
                            d="M164.96 300.004h.024c.02 0 .04-.004.059-.004H437a15.003 15.003 0 0 0 14.422-10.879l60-210a15.003 15.003 0 0 0-2.445-13.152A15.006 15.006 0 0 0 497 60H130.367l-10.722-48.254A15.003 15.003 0 0 0 105 0H15C6.715 0 0 6.715 0 15s6.715 15 15 15h77.969c1.898 8.55 51.312 230.918 54.156 243.71C131.184 280.64 120 296.536 120 315c0 24.812 20.188 45 45 45h272c8.285 0 15-6.715 15-15s-6.715-15-15-15H165c-8.27 0-15-6.73-15-15 0-8.258 6.707-14.977 14.96-14.996zM477.114 90l-51.43 180H177.032l-40-180zM150 405c0 24.813 20.188 45 45 45s45-20.188 45-45-20.188-45-45-45-45 20.188-45 45zm45-15c8.27 0 15 6.73 15 15s-6.73 15-15 15-15-6.73-15-15 6.73-15 15-15zm167 15c0 24.813 20.188 45 45 45s45-20.188 45-45-20.188-45-45-45-45 20.188-45 45zm45-15c8.27 0 15 6.73 15 15s-6.73 15-15 15-15-6.73-15-15 6.73-15 15-15zm0 0"
                            fill="currentColor"
                          ></path>
                        </svg>
                        <span className="absolute left-auto -ml-1 -top-2 rounded-full bg-red-500 px-1 py-0 text-xs text-white">
                          {cartItems?.cartItemCount
                            ? cartItems?.cartItemCount
                            : "0"}
                        </span>
                      </Menu.Button>

                      <Suspense>
                        <Cart
                          dispatch={dispatch}
                          isCartLoading={isLoading}
                          setIsLoading={setIsLoading}
                        />
                      </Suspense>
                    </Menu>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="flex md:hidden bg-light-forground dark:bg-dark-background mt-3 items-center border max-md:order-1 border-transparent focus-within:border-black dark:focus-within:border-gray-600 focus-within:bg-transparent px-4 h-11 min-w-[40%] lg:w-2/4 max-md:w-full transition-all rounded-full">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 192.904 192.904"
            className="fill-gray-400 dark:fill-gray-500 mr-4 w-4 h-4"
          >
            <path d="m190.707 180.101-47.078-47.077c11.702-14.072 18.752-32.142 18.752-51.831C162.381 36.423 125.959 0 81.191 0 36.422 0 0 36.423 0 81.193c0 44.767 36.422 81.187 81.191 81.187 19.688 0 37.759-7.049 51.831-18.751l47.079 47.078a7.474 7.474 0 0 0 5.303 2.197 7.498 7.498 0 0 0 5.303-12.803zM15 81.193C15 44.694 44.693 15 81.191 15c36.497 0 66.189 29.694 66.189 66.193 0 36.496-29.692 66.187-66.189 66.187C44.693 147.38 15 117.689 15 81.193z"></path>
          </svg>
          <input
            type="email"
            placeholder="Search..."
            className="w-full rounded-full outline-none bg-transparent text-black dark:text-white text-sm placeholder:text-gray-500 dark:placeholder:text-gray-400"
          />
        </div>

        <section className="relative">
          <div className="sm:px-10 md:px-12 px-4 lg:px-16">
            <div className="items-center justify-between px-4 pt-2 bg-white dark:bg-dark-background mt-4 hidden md:flex">
              <div className="flex space-x-4 text-gray-700 dark:text-gray-300 text-sm">
                <Link
                  to="gift-cards"
                  onMouseEnter={() => {
                    activeTabNumber.current = 1;
                    setDropdownNoOpen(1);
                  }}
                  onMouseLeave={() => setDropdownNoOpen(0)}
                  className={`pb-3 pt-3 group font-bold relative ${
                    isGiftCardsActive || isGiftCardsSubRoute
                      ? "text-light-primary-600 dark:text-light-primary-400"
                      : "text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  }`}
                >
                  Gift Cards
                  <span
                    className={`absolute left-0 bottom-0 w-full h-[2px] bg-light-primary-600 transform transition-transform duration-300 ${
                      dropdownNoOpen === 1 ||
                      isGiftCardsActive ||
                      isGiftCardsSubRoute
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                <Link
                  to="refill"
                  onMouseEnter={() => {
                    activeTabNumber.current = 2;
                    setDropdownNoOpen(2);
                  }}
                  onMouseLeave={() => setDropdownNoOpen(0)}
                  className={`pb-3 pt-3 group font-bold relative ${
                    isRefillActive
                      ? "text-light-primary-600 dark:text-light-primary-400"
                      : "text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  }`}
                >
                  Phone Refill
                  <span
                    className={`absolute left-0 bottom-0 w-full h-[2px] bg-light-primary-600 transform transition-transform duration-300 ${
                      dropdownNoOpen === 2 || isRefillActive
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                <Link
                  to="/international-topup"
                  className={`pb-3 pt-3 group font-bold relative whitespace-nowrap ${
                    isInternationalActive
                      ? "text-light-primary-600 dark:text-light-primary-400"
                      : "text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  }`}
                >
                  International Top-up
                  <span
                    className={`absolute left-0 bottom-0 w-full h-[2px] bg-light-primary-600 origin-left transform transition-transform duration-300 ${
                      isInternationalActive
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                <Link
                  to="gift-cards/Gaming"
                  onMouseEnter={() => {
                    activeTabNumber.current = 3;
                    setDropdownNoOpen(3);
                  }}
                  onMouseLeave={() => setDropdownNoOpen(0)}
                  className={`pb-3 pt-3 group font-bold relative ${
                    isGamingActive
                      ? "text-light-primary-600 dark:text-light-primary-400"
                      : "text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  }`}
                >
                  Gaming
                  <span
                    className={`absolute left-0 bottom-0 w-full h-[2px] bg-light-primary-600 transform transition-transform duration-300 ${
                      dropdownNoOpen === 3 || isGamingActive
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>

                <Link
                  to="gift-cards/travel"
                  className={`relative group pb-3 pt-3 font-bold ${
                    isTravelActive
                      ? "text-light-primary-600 dark:text-light-primary-400"
                      : "text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white"
                  }`}
                >
                  Travel
                  <span
                    className={`absolute left-0 bottom-0 w-full h-[2px] bg-light-primary-600 origin-left transform transition-transform duration-300 ${
                      isTravelActive
                        ? "scale-x-100"
                        : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  ></span>
                </Link>
              </div>
            </div>
          </div>

          <div
            id="dropdownDelay"
            onMouseEnter={() => setDropdownNoOpen(activeTabNumber.current || 1)}
            onMouseLeave={() => setDropdownNoOpen(0)}
            className={`mega-menu-section shadow-2xl absolute z-[999] top-[69px] left-0 right-0 w-full ${
              dropdownNoOpen > 0 ? "block " : "hidden"
            }`}
          >
            {dropdownNoOpen === 1 ? (
              <GiftsMegaMenu
                dispatch={dispatch}
                handleCloseDropdowns={() => setDropdownNoOpen(0)}
              />
            ) : null}
            {dropdownNoOpen === 2 ? (
              <RefillsMegaMenu
                dispatch={dispatch}
                handleCloseDropdowns={() => setDropdownNoOpen(0)}
              />
            ) : null}
            {dropdownNoOpen === 3 ? (
              <GamingMegaMenu
                dispatch={dispatch}
                handleCloseDropdowns={() => setDropdownNoOpen(0)}
              />
            ) : null}
          </div>
        </section>

        <Suspense>
          <MyModal
            isOpen={isLanguage}
            onClose={() => {
              setIsLanguage(false);
            }}
            isActionBtn={false}
          >
            <div
              style={{ flex: 1, minHeight: 255 }}
              className="dark:bg-gray-900"
            >
              <Suspense>
                <CountrySelector
                  handleLanguageChange={handleLanguageChange}
                  onClose={() => setIsLanguage(false)}
                  onSave={(e: any) => {
                    setIsLanguage(false);
                  }}
                />
              </Suspense>
            </div>
          </MyModal>
        </Suspense>
      </header>

      {/* Mobile Sidebar Overlay */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out md:hidden ${
          isSidebarOpen ? "opacity-50 z-40" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setIsSidebarOpen(false)}
      />

      {/* Mobile Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white dark:bg-dark-background z-50 transform transition-all duration-300 ease-in-out md:hidden ${
          isSidebarOpen ? "translate-x-0 shadow-xl" : "-translate-x-full"
        }`}
      >
        <div className="p-4">
          <div className="flex justify-between items-center mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/imgs/logo.svg`}
              alt="logo"
              className="w-32 dark:invert"
            />
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <nav className="space-y-4">
            <Link
              to="gift-cards"
              onClick={() => setIsSidebarOpen(false)}
              className="block py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white font-bold transition-colors"
            >
              Gift Cards
            </Link>
            <Link
              to="#"
              onClick={() => setIsSidebarOpen(false)}
              className="block py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white font-bold transition-colors"
            >
              Phone Refill
            </Link>
            {/*
              The three above link to "#" and always have. Left as they are
              rather than changed in passing -- this one is new, so it gets a
              real destination.
            */}
            <Link
              to="/international-topup"
              onClick={() => setIsSidebarOpen(false)}
              className="block py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white font-bold transition-colors"
            >
              International Top-up
            </Link>
            <Link
              to="#"
              onClick={() => setIsSidebarOpen(false)}
              className="block py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white font-bold transition-colors"
            >
              Gaming
            </Link>
            <Link
              to="#"
              onClick={() => setIsSidebarOpen(false)}
              className="block py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white font-bold transition-colors"
            >
              Travel
            </Link>
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <Link
                to="/signin"
                onClick={() => setIsSidebarOpen(false)}
                className="flex items-center py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/icons/users.svg`}
                  className="w-6 h-6 mr-2 dark:invert"
                  alt="login-page-logo"
                />
                Login
              </Link>
              <div
                className="flex items-center py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white cursor-pointer transition-colors"
                onClick={() => {
                  setIsSidebarOpen(false);
                  setIsLanguage(true);
                }}
              >
                <img
                  src={flagUrl("de")}
                  className="w-6 h-6 mr-2"
                  alt="language-selector-logo"
                />
                EN
              </div>
              <div className="flex items-center py-2 text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white transition-colors">
                <img
                  src={`${process.env.PUBLIC_URL}/icons/help.svg`}
                  className="w-6 h-6 mr-2 dark:invert"
                  alt="help-page-logo"
                />
                Help
              </div>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
}

export default Header;
